import { useEffect, useState, useContext } from 'react'
import moment from 'moment'

import LoadingContext from '../../context/Loading/Loading'

import {
  formatCurrency,
  formatDateTime,
  getDefaultSeason,
  createWorkbook,
  saveWorkbook,
  addWorksheet,
  setHeaders,
  setColumnsAndRows,
  currencyFormat,
  DEFAULTNUMHEADERS,
} from '../../utilities/helpers'

import { getSeasons, getDeferredRevenueReport } from '../../utilities/api'
import { useReportRefresh } from '../../hooks/useReportRefresh'

import Alert from 'react-bootstrap/Alert'
import Card from 'react-bootstrap/Card'
import Table from 'react-bootstrap/Table'
import Form from 'react-bootstrap/Form'

import { ReportActions } from '../ReportActions'
import { SeasonSelector } from '../SeasonSelector'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { EmptyContainer } from '../EmptyContainer'
import { LoadingContainer } from '../LoadingContainer'
import DeferredRevenueEmailSchedule from './DeferredRevenueEmailSchedule'

export default function DeferredRevenueWrapper({ organization }) {
  const { showLoading, hideLoading, isLoading } = useContext(LoadingContext)

  const [seasons, setSeasons] = useState()
  const [selectedSeason, setSelectedSeason] = useState()
  const [asOfDate, setAsOfDate] = useState(() => {
    return moment().startOf('day').toDate()
  })
  const [includeAdvanceTickets, setIncludeAdvanceTickets] = useState(true)
  const [isLoadingData, setIsLoadingData] = useState(false)
  const { refreshToken, refresh, completeRefresh } =
    useReportRefresh(isLoadingData)
  const [data, setData] = useState(null)

  const loadSeasons = () => {
    getSeasons(organization.uuid)
      .then((res) => {
        setSeasons(res.data)
        const selected = getDefaultSeason(res.data)
        if (selected) setSelectedSeason(selected?.id)
        hideLoading()
      })
      .catch((err) => {
        console.error(err)
        hideLoading()
      })
  }

  const loadReport = () => {
    if (!selectedSeason || !asOfDate) return
    setIsLoadingData(true)
    getDeferredRevenueReport(
      organization.uuid,
      selectedSeason,
      asOfDate.toISOString(),
      undefined,
      includeAdvanceTickets
    )
      .then((res) => {
        setData(res.data)
        setIsLoadingData(false)
        completeRefresh()
      })
      .catch((err) => {
        console.error(err)
        setIsLoadingData(false)
        completeRefresh()
      })
  }

  useEffect(() => {
    showLoading()
    loadSeasons()
  }, [])

  useEffect(() => {
    if (selectedSeason && asOfDate) loadReport()
  }, [selectedSeason, asOfDate, includeAdvanceTickets, refreshToken])

  const handleExcelExport = async () => {
    if (!data) return

    const workbook = createWorkbook()
    const asOfStr = formatDateTime(moment(asOfDate), 'dateOnly')
    const headers = [
      `As of ${asOfStr}`,
      `Period: ${formatDateTime(moment(data.periodStart), 'dateOnly')} – ${formatDateTime(moment(data.periodEnd), 'dateOnly')}`,
    ]

    const rollforwardSheet = addWorksheet('Rollforward', workbook)
    setHeaders(
      'Season Deferred Revenue Rollforward',
      organization?.name || '',
      rollforwardSheet,
      headers
    )
    const rf = data.rollforward || {}
    const rollforwardRows = [
      {
        line: 'Opening deferred revenue',
        amount: rf.openingDeferred,
      },
      {
        line: '+ Collections in period',
        amount: rf.collectionsInPeriod,
      },
      {
        line: '− Recognized this period (games played)',
        amount: rf.recognizedInPeriod,
      },
      {
        line: '= Closing deferred revenue',
        amount: rf.closingDeferred,
      },
      {
        line: 'Journal entry (Dr Deferred / Cr Ticket Revenue)',
        amount: rf.journalEntry?.amount,
      },
    ]
    setColumnsAndRows(
      ['line', 'amount'],
      rollforwardRows,
      (row) => ({
        line: row.line,
        amount: Number(row.amount || 0),
      }),
      rollforwardSheet
    )
    currencyFormat(2, rollforwardSheet, DEFAULTNUMHEADERS + headers.length)

    const byPackageSheet = addWorksheet('By Package', workbook)
    setHeaders(
      'Season Deferred Revenue by Package',
      organization?.name || '',
      byPackageSheet,
      headers
    )
    setColumnsAndRows(
      [
        'package_name',
        'product_type',
        'games',
        'orders',
        'allocated',
        'earned',
        'unearned',
      ],
      data.byPackage || [],
      (row) => ({
        package_name: row.packageName,
        product_type: row.productType,
        games: row.gameCount,
        orders: row.orderCount,
        allocated: Number(row.allocated || 0),
        earned: Number(row.earned || 0),
        unearned: Number(row.unearned || 0),
      }),
      byPackageSheet
    )
    currencyFormat(5, byPackageSheet, DEFAULTNUMHEADERS + headers.length)
    currencyFormat(6, byPackageSheet, DEFAULTNUMHEADERS + headers.length)
    currencyFormat(7, byPackageSheet, DEFAULTNUMHEADERS + headers.length)

    const byGameSheet = addWorksheet('By Game', workbook)
    setHeaders(
      'Season Deferred Revenue by Game',
      organization?.name || '',
      byGameSheet,
      headers
    )
    setColumnsAndRows(
      ['event_name', 'game_date', 'allocated', 'earned', 'unearned', 'status'],
      data.byGame || [],
      (row) => ({
        event_name: row.eventName,
        game_date: row.recognitionDate
          ? formatDateTime(moment(row.recognitionDate), 'dateOnly')
          : '',
        allocated: Number(row.allocated || 0),
        earned: Number(row.earned || 0),
        unearned: Number(row.unearned || 0),
        status: row.status,
      }),
      byGameSheet
    )
    currencyFormat(3, byGameSheet, DEFAULTNUMHEADERS + headers.length)
    currencyFormat(4, byGameSheet, DEFAULTNUMHEADERS + headers.length)
    currencyFormat(5, byGameSheet, DEFAULTNUMHEADERS + headers.length)

    saveWorkbook(workbook, 'season-deferred-revenue-report')
      .then()
      .catch((err) => alert(err.message))
  }

  return (
    <>
      {isLoading ? (
        <PageLoadingContainer style="without-sidebar" />
      ) : (
        <section className="spacer-sm">
          <header className="section-header-sm">
            <div className="section-heading">
              <h1>Season Deferred Revenue</h1>
            </div>
            <p className="section-header-desc">
              Month-end unearned ticket revenue and recognition schedule. Season
              packages, advance sales, and flex-pack redemptions sit as deferred
              revenue until each game starts. Per-game allocated amounts use the
              same Ticket Proceeds net as Access Control and Event Sales &
              Attendance. Pick a season and an as-of date (defaults to today) to
              close the period.
            </p>
          </header>

          {seasons?.length > 0 ? (
            <>
              <ReportActions
                isLoading={isLoadingData}
                onRefresh={refresh}
                isRefreshing={isLoadingData}
                selected={selectedSeason}
                startDate={asOfDate}
                setStartDate={setAsOfDate}
                showTime={false}
                data={data}
                downloadReport={handleExcelExport}
              >
                <SeasonSelector
                  seasons={seasons}
                  selected={selectedSeason}
                  setSelected={setSelectedSeason}
                  isLoading={isLoadingData}
                />
              </ReportActions>

              <Form.Check
                type="switch"
                id="include-advance-tickets"
                className="mb-4"
                label="Include advance single-game ticket sales"
                checked={includeAdvanceTickets}
                disabled={isLoadingData}
                onChange={(e) => setIncludeAdvanceTickets(e.target.checked)}
              />

              <DeferredRevenueEmailSchedule
                organizationUUID={organization?.uuid}
                selectedSeason={selectedSeason}
                includeAdvanceTickets={includeAdvanceTickets}
                asOfDate={asOfDate}
              />

              <Results
                isLoading={isLoadingData}
                data={data}
                asOfDate={asOfDate}
              />
            </>
          ) : (
            <Card body>
              <EmptyContainer style="center lg">
                <p>No seasons created</p>
                <p className="subtitle">
                  Create a season with packages to track deferred revenue.
                </p>
              </EmptyContainer>
            </Card>
          )}
        </section>
      )}
    </>
  )
}

function getFirstGameDate(byGame = []) {
  const dates = byGame
    .map((row) => (row.recognitionDate ? moment(row.recognitionDate) : null))
    .filter((d) => d && d.isValid())
    .sort((a, b) => a.valueOf() - b.valueOf())
  return dates[0] || null
}

function Results({ isLoading, data, asOfDate }) {
  if (!asOfDate) {
    return (
      <Card body>
        <EmptyContainer style="center lg">
          <p>Select an as-of date</p>
          <p className="subtitle">
            Choose the month-end date for your Balance Sheet close.
          </p>
        </EmptyContainer>
      </Card>
    )
  }

  if (isLoading) {
    return (
      <Card body>
        <LoadingContainer />
      </Card>
    )
  }

  if (!data) {
    return (
      <Card body>
        <EmptyContainer style="center lg">
          <p>No season deferred revenue data</p>
        </EmptyContainer>
      </Card>
    )
  }

  const rf = data.rollforward || {}
  const summary = data.summary || {}
  const byGame = data.byGame || []
  const firstGame = getFirstGameDate(byGame)
  const asOfMoment = moment(asOfDate).endOf('day')
  const isBeforeFirstGame = firstGame && asOfMoment.isBefore(firstGame, 'day')
  const gamesEarned = byGame.filter((g) => Number(g.earned || 0) > 0).length
  const gamesRemaining = byGame.filter(
    (g) => Number(g.unearned || 0) > 0
  ).length

  return (
    <>
      {isBeforeFirstGame ? (
        <Alert variant="info" className="mb-4">
          <strong>Season has not started yet as of this date.</strong> The first
          linked game is {formatDateTime(firstGame, 'dateOnly')}. Until that
          date passes, Earned stays $0.00 and Closing deferred revenue equals
          all collected season / advance ticket revenue. Move the as-of date
          past the first game to see recognition begin.
        </Alert>
      ) : null}

      <Card body className="card-with-border mb-4">
        <h2 className="card-title-sm mb-2">How to read this report</h2>
        <p className="text-muted small mb-0">
          Cash collected for season packages (and advance single-game tickets)
          is held as deferred revenue until each game starts. Allocable ticket
          revenue excludes service fees, estimated processing fees, and sales
          tax, then is split equally across the package games. Use the as-of
          date as your month-end Balance Sheet cut-off; the period defaults to
          the start of that month through the as-of date.
        </p>
      </Card>

      <div className="row g-3 mb-4">
        <SummaryCard
          label="Closing deferred revenue"
          value={summary.deferredRevenue}
          hint="Liability still unearned as of this date"
        />
        <SummaryCard
          label="Recognized this period"
          value={summary.recognizedThisPeriod}
          hint="Revenue released when games started this period"
        />
        <SummaryCard
          label="Collections this period"
          value={summary.collectionsThisPeriod}
          hint="New cash collected this period"
        />
        <SummaryCard
          label="Package games"
          value={summary.packageGameCount}
          hint={
            byGame.length
              ? `${gamesEarned} earned · ${gamesRemaining} still deferred`
              : 'Equal share per linked game'
          }
          isCurrency={false}
        />
      </div>

      <Card body className="card-with-border mb-4">
        <h2 className="card-title-sm mb-2">Month-end rollforward</h2>
        <p className="text-muted small mb-3">
          Opening deferred + collections − recognized = closing deferred.
          Closing should match your Balance Sheet deferred revenue liability.
          Recognized is the suggested Dr Deferred Revenue / Cr Ticket Revenue
          amount for the period.
        </p>
        <p className="text-muted small mb-3">
          Period {formatDateTime(moment(data.periodStart), 'dateOnly')} –{' '}
          {formatDateTime(moment(data.periodEnd), 'dateOnly')}
          {data.methodology?.allocableBase
            ? ` · Base: ${data.methodology.allocableBase}`
            : ''}
        </p>
        <Table responsive className="mb-0">
          <tbody>
            <RollforwardRow
              label="Opening deferred revenue"
              amount={rf.openingDeferred}
            />
            <RollforwardRow
              label="+ Collections in period"
              amount={rf.collectionsInPeriod}
            />
            <RollforwardRow
              label="− Revenue recognized (games played)"
              amount={rf.recognizedInPeriod}
            />
            <RollforwardRow
              label="= Closing deferred revenue"
              amount={rf.closingDeferred}
              strong
            />
          </tbody>
        </Table>
        {rf.journalEntry?.amount > 0 && (
          <p className="mt-3 mb-0 small text-muted">
            Suggested journal entry: Dr{' '}
            <strong>{rf.journalEntry.debitAccount}</strong>{' '}
            {formatCurrency(rf.journalEntry.amount)} / Cr{' '}
            <strong>{rf.journalEntry.creditAccount}</strong>{' '}
            {formatCurrency(rf.journalEntry.amount)}
          </p>
        )}
        {rf.journalEntry?.amount === 0 && isBeforeFirstGame ? (
          <p className="mt-3 mb-0 small text-muted">
            No journal entry yet — no games have started as of this date, so
            nothing has been recognized.
          </p>
        ) : null}
      </Card>

      <Card body className="card-with-border mb-4">
        <h2 className="card-title-sm mb-2">By package</h2>
        <p className="text-muted small mb-3">
          One row per season package (and advance tickets, if included).
          Allocated is total ticket consideration collected for that product.
          Earned is the portion for games that have already started; Unearned is
          still sitting as deferred revenue. Earned + Unearned should equal
          Allocated.
        </p>
        {(data.byPackage || []).length === 0 ? (
          <EmptyContainer style="center">
            <p>No package allocations for this period</p>
          </EmptyContainer>
        ) : (
          <Table responsive hover className="mb-0">
            <thead>
              <tr>
                <th>Package</th>
                <th>Type</th>
                <th className="text-end">Games</th>
                <th className="text-end">Orders</th>
                <th className="text-end">Allocated</th>
                <th className="text-end">Earned</th>
                <th className="text-end">Unearned</th>
              </tr>
            </thead>
            <tbody>
              {data.byPackage.map((row) => (
                <tr
                  key={`${row.productType}-${row.packageId || row.packageName}`}
                >
                  <td>{row.packageName}</td>
                  <td>
                    {row.productType === 'advance_ticket'
                      ? 'Advance tickets'
                      : 'Season package'}
                  </td>
                  <td className="text-end">{row.gameCount}</td>
                  <td className="text-end">{row.orderCount}</td>
                  <td className="text-end">{formatCurrency(row.allocated)}</td>
                  <td className="text-end">{formatCurrency(row.earned)}</td>
                  <td className="text-end">{formatCurrency(row.unearned)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Card body className="card-with-border">
        <h2 className="card-title-sm mb-2">By game</h2>
        <p className="text-muted small mb-3">
          Recognition schedule by event start date. Status is earned once the
          game date is on or before the as-of date, unearned if it is still in
          the future, or partial when some orders for that game are earned and
          others are not. Amounts are the equal per-game share of each package
          (or the full advance-ticket amount for single-game sales).
        </p>
        {(data.byGame || []).length === 0 ? (
          <EmptyContainer style="center">
            <p>No game schedule allocations</p>
          </EmptyContainer>
        ) : (
          <Table responsive hover className="mb-0">
            <thead>
              <tr>
                <th>Event</th>
                <th>Game date</th>
                <th className="text-end">Allocated</th>
                <th className="text-end">Earned</th>
                <th className="text-end">Unearned</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.byGame.map((row) => (
                <tr key={row.eventId || row.eventName}>
                  <td>{row.eventName}</td>
                  <td>
                    {row.recognitionDate
                      ? formatDateTime(moment(row.recognitionDate), 'dateOnly')
                      : '—'}
                  </td>
                  <td className="text-end">{formatCurrency(row.allocated)}</td>
                  <td className="text-end">{formatCurrency(row.earned)}</td>
                  <td className="text-end">{formatCurrency(row.unearned)}</td>
                  <td className="text-capitalize">{row.status}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  )
}

function SummaryCard({ label, value, hint, isCurrency = true }) {
  return (
    <div className="col-6 col-lg-3">
      <Card body className="card-with-border h-100">
        <p className="small text-muted mb-1">{label}</p>
        <p className="h4 mb-1">
          {isCurrency
            ? formatCurrency(value || 0)
            : value != null
              ? value
              : '—'}
        </p>
        {hint ? <p className="small text-muted mb-0">{hint}</p> : null}
      </Card>
    </div>
  )
}

function RollforwardRow({ label, amount, strong }) {
  return (
    <tr>
      <td className={strong ? 'fw-semibold' : ''}>{label}</td>
      <td className={`text-end ${strong ? 'fw-semibold' : ''}`}>
        {formatCurrency(amount || 0)}
      </td>
    </tr>
  )
}
